import type { FunctionComponentWithChildren } from '@/common';
import type {
  Cell,
  ExpandedState,
  OnChangeFn,
  RowData,
  RowSelectionState,
  SortingState,
  TableOptions,
} from '@tanstack/react-table';
import type { ComponentProps, FunctionComponent } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';

import type { SortDirection } from '@ballerine/common';
import { isInstanceOfFunction } from '@ballerine/common';

import { ctw } from '@/common';
import { checkIsBooleanishRecord } from '@/common/utils/check-is-booleanish-record/check-is-booleanish-record';
import {
  Collapsible,
  ScrollArea,
  CollapsibleContent as ShadCNCollapsibleContent,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components';
import { DefaultTableCell } from '@/components/atoms/DefaultTableCell';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Its external module that uses interface
  export interface ColumnMeta<TData extends RowData, TValue> {
    useWrapper?: boolean;
    headerClassName?: string;
  }
}

export type IDataTableProps<TData extends RowData> = {
  data: TData[];
  columns: Parameters<typeof useReactTable<TData>>[0]['columns'];
  caption?: ComponentProps<typeof TableCaption>['children'];

  CellContentWrapper?: FunctionComponentWithChildren<{ cell: Cell<TData, unknown> }>;
  CollapsibleContent?: FunctionComponent<{ row: TData }>;

  // Component props
  props?: {
    scroll?: Partial<ComponentProps<typeof ScrollArea>>;
    table?: ComponentProps<typeof Table>;
    header?: ComponentProps<typeof TableHeader>;
    head?: ComponentProps<typeof TableHead>;
    row?: ComponentProps<typeof TableRow>;
    body?: ComponentProps<typeof TableBody>;
    cell?: ComponentProps<typeof TableCell>;
    noDataCell?: ComponentProps<typeof TableCell>;
    caption?: ComponentProps<typeof TableCaption>;
    wrapper?: ComponentProps<'div'>;
  };

  // react-table options
  options?: Omit<TableOptions<TData>, 'getCoreRowModel' | 'data' | 'columns'>;

  sort?: {
    onSort: (params: { sortBy: string; sortDir: SortDirection }) => void;
    sortDir: SortDirection | undefined;
    sortBy: string | undefined;
  };

  select: {
    onSelect: (ids: Record<string, boolean>) => void;
    onSelectAll?: (value: boolean) => void;
    selected: Record<string, boolean>;
    isSelectAll?: boolean;
  };

  // External sorting state management (for backend sorting)
  sortingState?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;

  scrollRef?: React.RefObject<HTMLDivElement>;
  handleScroll?: (event: React.UIEvent<HTMLDivElement>) => void;

  // Row memoization
  rowMemoComparator?: (prev: TData, next: TData) => boolean;
};

type MemoizedTableRowProps<TData extends RowData> = {
  row: ReturnType<ReturnType<typeof useReactTable<TData>>['getRowModel']>['rows'][number];
  // TanStack reuses `row` refs across renders, so `row.getIsSelected()` in a
  // memo comparator reads the same live value for both prev/next. Capture
  // `isSelected` at parent render so the comparator sees actual diffs.
  isSelected: boolean;
  rowProps?: ComponentProps<typeof TableRow>;
  CellContentWrapper?: FunctionComponentWithChildren<{ cell: Cell<TData, unknown> }>;
  cellProps?: ComponentProps<typeof TableCell>;
  rowMemoComparator?: (prev: TData, next: TData) => boolean;
  CollapsibleContent?: FunctionComponent<{ row: TData }>;
};

const TableRowContent = <TData extends RowData>({
  row,
  rowProps,
  CellContentWrapper,
  cellProps,
  CollapsibleContent,
}: Omit<MemoizedTableRowProps<TData>, 'rowMemoComparator'>) => {
  return (
    <>
      <TableRow
        {...rowProps}
        data-testid="reports-table-row"
        data-row-id={row.id}
        className={ctw(
          'h-[76px] border-b-0 even:bg-[#F4F6FD]/50 hover:bg-[#F4F6FD]/90',
          CollapsibleContent && 'cursor-pointer',
          rowProps?.className,
        )}
        onClick={CollapsibleContent ? () => row.toggleExpanded() : rowProps?.onClick}
      >
        {row.getVisibleCells()?.map(cell => (
          <TableCell
            key={cell.id}
            {...cellProps}
            className={ctw('px-4 py-2', cellProps?.className)}
          >
            {CellContentWrapper && !cell.column.columnDef.meta?.useWrapper ? (
              <CellContentWrapper cell={cell}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </CellContentWrapper>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        ))}
      </TableRow>
      {CollapsibleContent && (
        <Collapsible open={row.getIsExpanded()} asChild>
          <ShadCNCollapsibleContent asChild>
            <TableRow className={`border-y-[1px]`}>
              <TableCell colSpan={10} className={`p-0`}>
                <div className="max-h-[228px] overflow-auto p-8">
                  <CollapsibleContent row={row.original} />
                </div>
              </TableCell>
            </TableRow>
          </ShadCNCollapsibleContent>
        </Collapsible>
      )}
    </>
  );
};

const createMemoizedTableRow = <TData extends RowData>(
  rowMemoComparator?: (prev: TData, next: TData) => boolean,
) => {
  if (!rowMemoComparator) {
    return TableRowContent<TData>;
  }

  return React.memo(TableRowContent<TData>, (prevProps, nextProps) => {
    if (prevProps.isSelected !== nextProps.isSelected) {
      return false;
    }

    return rowMemoComparator(prevProps.row.original, nextProps.row.original);
  }) as typeof TableRowContent<TData>;
};

const MemoizedTableRow = <TData extends RowData>({
  row,
  isSelected,
  rowProps,
  CellContentWrapper,
  cellProps,
  rowMemoComparator,
  CollapsibleContent,
}: MemoizedTableRowProps<TData>) => {
  const RowComponent = useMemo(
    () => createMemoizedTableRow<TData>(rowMemoComparator),
    [rowMemoComparator],
  );

  return (
    <RowComponent
      row={row}
      isSelected={isSelected}
      rowProps={rowProps}
      CellContentWrapper={CellContentWrapper}
      cellProps={cellProps}
      CollapsibleContent={CollapsibleContent}
    />
  );
};

const DataTableBase = <TData extends RowData>(
  {
    data,
    props,
    caption,
    columns,
    CellContentWrapper,
    options = {},
    CollapsibleContent,
    sort,
    select,
    sortingState,
    onSortingChange,
    handleScroll,
    rowMemoComparator,
  }: IDataTableProps<TData>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { enableSorting = false } = options;

  // Use external sorting state if provided, otherwise internal state
  const [internalSorting, setInternalSorting] = useState<SortingState>([
    {
      id: sort?.sortBy || options?.initialState?.sorting?.[0]?.id || 'id',
      desc: sort?.sortDir === 'desc' || options?.initialState?.sorting?.[0]?.desc || false,
    },
  ]);

  const sorting = sortingState || internalSorting;
  const setSorting = onSortingChange || setInternalSorting;

  useEffect(() => {
    if (sort?.sortBy && sort?.sortDir && !sortingState) {
      setInternalSorting([
        {
          id: sort.sortBy,
          desc: sort.sortDir === 'desc',
        },
      ]);
    }
  }, [sort?.sortBy, sort?.sortDir, sortingState]);

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    sortingUpdaterOrValue => {
      if (onSortingChange) {
        // Use external sorting state handler
        onSortingChange(sortingUpdaterOrValue);
      } else {
        // Legacy sorting with URL parameters
        setSorting(prevSortingState => {
          if (!isInstanceOfFunction(sortingUpdaterOrValue)) {
            sort?.onSort({
              sortBy: sortingUpdaterOrValue[0]?.id || sort?.sortBy || '',
              sortDir: sortingUpdaterOrValue[0]?.desc ? 'desc' : 'asc',
            });

            return sortingUpdaterOrValue;
          }

          const newSortingState = sortingUpdaterOrValue(prevSortingState);

          sort?.onSort({
            sortBy: newSortingState[0]?.id || sort?.sortBy || '',
            sortDir: newSortingState[0]?.desc ? 'desc' : 'asc',
          });

          return newSortingState;
        });
      }
    },
    [onSortingChange, setSorting, sort?.onSort, sort?.sortBy],
  );

  const { selected: ids, onSelect, onSelectAll, isSelectAll } = select;

  // Fully controlled: `select.selected` is the single source of truth.
  // Prior local-state + sync-effect setup had a residual-selection bug after
  // bulk-action clears (checkboxes stayed visually checked after the parent
  // reset `ids` to {}). Deriving via useMemo removes the race.
  const rowSelection = useMemo<RowSelectionState>(
    () => (checkIsBooleanishRecord(ids) ? ids : {}),
    [ids],
  );

  const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    selectionUpdaterOrValue => {
      const nextSelection = isInstanceOfFunction(selectionUpdaterOrValue)
        ? selectionUpdaterOrValue(rowSelection)
        : selectionUpdaterOrValue;

      onSelect(nextSelection);
    },
    [onSelect, rowSelection],
  );

  const state = useMemo(
    () => ({
      rowSelection,
      ...(enableSorting && {
        sorting,
      }),
      ...(CollapsibleContent && {
        expanded,
      }),
    }),
    [CollapsibleContent, enableSorting, expanded, rowSelection, sorting],
  );

  const table = useReactTable<TData>({
    state,
    ...options,
    data: data ?? [],
    columns: columns ?? [],
    onRowSelectionChange,
    enableRowSelection: true,
    getRowId: row => (row as TData & { id: string }).id,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      onSelectAll,
      isSelectAll,
    },
    defaultColumn: {
      cell: DefaultTableCell,
    },
    ...(enableSorting && {
      enableSorting,
      onSortingChange: handleSortingChange,
      manualSorting: true,
      sortDescFirst: true,
      enableSortingRemoval: false,
      getSortedRowModel: getSortedRowModel(),
    }),
    ...(CollapsibleContent
      ? {
          onExpandedChange: setExpanded,
          getExpandedRowModel: getExpandedRowModel(),
        }
      : {}),
  });

  return (
    <div
      className={ctw(
        'relative overflow-auto rounded-md border bg-white shadow',
        props?.wrapper?.className,
      )}
    >
      <ScrollArea orientation="both" {...props?.scroll} onScrollCapture={handleScroll} ref={ref}>
        <Table {...props?.table}>
          {caption && (
            <TableCaption
              {...props?.caption}
              className={ctw('text-foreground', props?.caption?.className)}
            >
              {caption}
            </TableCaption>
          )}
          <TableHeader className="border-0" {...props?.header}>
            {table.getHeaderGroups()?.map(({ id, headers }) => (
              <TableRow
                key={id}
                {...props?.row}
                className={ctw('border-b-none', props?.row?.className)}
              >
                {headers?.map(header => {
                  const isSelectColumn = header.column.id === 'select';

                  return (
                    <TableHead
                      key={header.id}
                      {...props?.head}
                      className={ctw(
                        // `px-4` is the default inter-column gutter so adjacent
                        // headers never butt against each other AND aligns
                        // with the common `px-4` cell-content padding used
                        // across backoffice-v2 tables; `whitespace-nowrap`
                        // pairs with the fixed `h-[34px]` so headers don't
                        // wrap and overflow the row.
                        'sticky top-0 z-0 h-[34px] whitespace-nowrap bg-white px-4 py-1 text-[14px] font-bold text-[#787981]',
                        {
                          // Select-column TH owns its own padding via the
                          // inner flex-center wrapper (its checkbox sits on
                          // the column's vertical axis).
                          '!p-0': isSelectColumn,
                        },
                        props?.head?.className,
                        header.column.columnDef.meta?.headerClassName,
                      )}
                    >
                      {isSelectColumn && !header.isPlaceholder && (
                        <div className="flex h-full items-center justify-end">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                      {header.column.getCanSort() && !header.isPlaceholder && !isSelectColumn && (
                        <button
                          className={ctw(
                            // `!w-full` overrides any width class merged in from
                            // `meta.headerClassName` (which is intended for the
                            // TH only). Without `!`, e.g. `w-[1%]` would shrink
                            // the button and clip the sort chevron.
                            'flex h-9 !w-full flex-row items-center gap-x-2 text-[#A3A3A3]',
                            header.column.columnDef.meta?.headerClassName || 'justify-start',
                          )}
                          onClick={() => header.column.toggleSorting()}
                        >
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          <ChevronDown
                            className={ctw('d-4', {
                              'rotate-180': header.column.getIsSorted() === 'asc',
                            })}
                          />
                        </button>
                      )}
                      {!header.column.getCanSort() &&
                        !header.isPlaceholder &&
                        !isSelectColumn &&
                        flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody {...props?.body}>
            {!!table.getRowModel().rows?.length &&
              table.getRowModel().rows?.map(row => (
                <React.Fragment key={row.id}>
                  <MemoizedTableRow
                    row={row}
                    isSelected={row.getIsSelected()}
                    rowProps={props?.row}
                    CellContentWrapper={CellContentWrapper}
                    cellProps={props?.cell}
                    rowMemoComparator={rowMemoComparator}
                    CollapsibleContent={CollapsibleContent}
                  />
                </React.Fragment>
              ))}
            {!table.getRowModel().rows?.length && (
              <TableRow
                {...props?.row}
                className={ctw('hover:bg-unset h-6 border-none', props?.row?.className)}
              >
                <TableCell
                  colSpan={columns?.length}
                  {...props?.noDataCell}
                  className={ctw('p-4', props?.noDataCell?.className)}
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
};

const forward = React.forwardRef as <T, P = NonNullable<unknown>>(
  render: (props: P, ref: React.Ref<T>) => React.ReactNode,
) => (props: P & React.RefAttributes<T>) => React.ReactNode;
export const DataTable = forward(DataTableBase);
